import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { ArrowUp, ArrowDown, Funnel, FilePdf } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  invoice_create: { label: 'Création facture', color: 'bg-red-100 text-red-700' },
  invoice_update: { label: 'Modification facture', color: 'bg-orange-100 text-orange-700' },
  invoice_delete: { label: 'Suppression facture', color: 'bg-green-100 text-green-700' },
  manual_adjustment: { label: 'Ajustement manuel', color: 'bg-blue-100 text-blue-700' },
  initial: { label: 'Stock initial', color: 'bg-slate-100 text-slate-700' }
};

const StockMovements = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMovements();
  }, []);

  const fetchMovements = async (filters = {}) => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/stock-movements`;
      const params = new URLSearchParams();
      if (filters.product_type && filters.product_type !== 'all') {
        params.append('product_type', filters.product_type);
      }
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { withCredentials: true });
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchMovements({
      product_type: productTypeFilter,
      start_date: startDate,
      end_date: endDate
    });
  };

  const clearFilters = () => {
    setProductTypeFilter('all');
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    fetchMovements();
  };

  const filteredMovements = movements.filter(m => 
    !searchQuery || m.product_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const downloadInventoryPdf = async (onlyLowStock = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/pdf?only_low_stock=${onlyLowStock}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      downloadBlob(response.data, `inventaire_${onlyLowStock ? 'stock_bas' : 'complet'}.pdf`, 'application/pdf');
      toast.success('Inventaire téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="stock-movements-page">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Mouvements de stock</h1>
            <p className="text-slate-600">Historique des entrées et sorties de stock</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => downloadInventoryPdf(false)}
              data-testid="download-inventory-pdf-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              <FilePdf size={18} weight="bold" className="mr-2" />
              Inventaire complet (PDF)
            </Button>
            <Button
              onClick={() => downloadInventoryPdf(true)}
              data-testid="download-low-stock-pdf-btn"
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <FilePdf size={18} className="mr-2" />
              Stock bas (PDF)
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Recherche produit</label>
              <Input
                type="text"
                placeholder="Nom du produit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-product-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de produit</label>
              <Select value={productTypeFilter} onValueChange={setProductTypeFilter}>
                <SelectTrigger data-testid="filter-product-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="key">Clés</SelectItem>
                  <SelectItem value="it_product">Produits IT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={applyFilters}
                data-testid="apply-filters-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white"
              >
                <Funnel size={18} className="mr-2" />
                Filtrer
              </Button>
              <Button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                variant="outline"
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Produit</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Variation</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Stock après</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Motif</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Référence</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMovements.map((movement) => {
                    const reason = REASON_LABELS[movement.reason] || { label: movement.reason, color: 'bg-slate-100 text-slate-700' };
                    return (
                      <tr key={movement.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {new Date(movement.created_at).toLocaleString('fr-FR')}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{movement.product_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {movement.product_type === 'key' ? 'Clé' : 'Produit IT'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className={`inline-flex items-center gap-1 font-medium ${
                            movement.quantity_change > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {movement.quantity_change > 0 ? <ArrowUp size={16} weight="bold" /> : <ArrowDown size={16} weight="bold" />}
                            {Math.abs(movement.quantity_change)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{movement.stock_after}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${reason.color}`}>
                            {reason.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {movement.reference_number || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{movement.user}</td>
                      </tr>
                    );
                  })}
                  {filteredMovements.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-slate-500">
                        Aucun mouvement de stock trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockMovements;
